// src/pages/LandingPage.js
// Entry point — choose login type then authenticate
// Three roles: Vendor | Mall Admin | Super Admin

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const ROLES = [
  {
    id:    'VENDOR',
    icon:  '🏪',
    title: 'Vendor Login',
    sub:   'Access your stall — menu, orders, kitchen, analytics',
    color: '#185FA5',
    bg:    '#1a2d3d',
  },
  {
    id:    'MALL_ADMIN',
    icon:  '🏬',
    title: 'Mall Admin Login',
    sub:   'Manage your food court — vendors, kiosk, settlement',
    color: '#854F0B',
    bg:    '#3d2e10',
  },
  {
    id:    'SUPER_ADMIN',
    icon:  '⚡',
    title: 'Super Admin Login',
    sub:   'Platform management — pricing, deployment, tenants',
    color: '#534AB7',
    bg:    '#2a1a3d',
  },
];

export default function LandingPage() {
  const { loginVendor, loginMall, loginSuperAdmin } = useAuth();
  const [selectedRole, setSelectedRole] = useState(null);
  // Self-signup is off unless the server says otherwise; any error keeps it off.
  const [config, setConfig] = useState({ selfSignupEnabled: false, contactEmail: null });
  const { selfSignupEnabled, contactEmail } = config;
  useEffect(() => {
    api.get('/config/signup-status')
      .then(r => setConfig({
        selfSignupEnabled: r.data?.selfSignupEnabled === true,
        contactEmail:      r.data?.contactEmail || null,
      }))
      .catch(() => setConfig({ selfSignupEnabled: false, contactEmail: null }));
  }, []);
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  const selectedConfig = ROLES.find(r => r.id === selectedRole);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return setError('Email and password required');
    setLoading(true); setError('');
    try {
      if (selectedRole === 'VENDOR')      await loginVendor(email, password);
      if (selectedRole === 'MALL_ADMIN')  await loginMall(email, password);
      if (selectedRole === 'SUPER_ADMIN') await loginSuperAdmin(email, password);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid credentials');
    } finally { setLoading(false); }
  };

  return (
    <div style={{
      minHeight:'100vh', background:'#0a0f1e',
      display:'flex', alignItems:'center', justifyContent:'center',
      padding:24, fontFamily:"'Segoe UI',system-ui,sans-serif",
    }}>
      <div style={{ width:'100%', maxWidth: selectedRole ? 420 : 700 }}>

        {/* Brand */}
        <div style={{ textAlign:'center', marginBottom:40 }}>
          <div style={{ fontSize:42, fontWeight:800, color:'#F59E0B', letterSpacing:-1 }}>⚡ QuickServe</div>
          <div style={{ fontSize:14, color:'#64748B', marginTop:6 }}>Food Court Intelligence Platform</div>
        </div>

        {/* Role selection */}
        {!selectedRole && (
          <>
            <div style={{ fontSize:16, fontWeight:600, color:'#CBD5E1', textAlign:'center', marginBottom:20 }}>
              Select your login type
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              {ROLES.map(role => (
                <div key={role.id}
                  onClick={() => { setSelectedRole(role.id); setError(''); }}
                  style={{
                    display:'flex', alignItems:'center', gap:20, padding:'20px 24px',
                    borderRadius:14, cursor:'pointer', transition:'all .2s',
                    background: role.bg,
                    border: `1px solid ${role.color}`,
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'translateX(4px)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'none'}
                >
                  <span style={{ fontSize:36 }}>{role.icon}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:16, color:'#F0EDE8' }}>{role.title}</div>
                    <div style={{ fontSize:13, color:'#94A3B8', marginTop:3 }}>{role.sub}</div>
                  </div>
                  <span style={{ color: role.color, fontSize:20 }}>→</span>
                </div>
              ))}
            </div>

            {/* New vendor sign-up CTA */}
            <div style={{ marginTop:24, padding:'16px 20px', background:'#1a2030', borderRadius:12, border:'1px solid #2a3050', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
              <div>
                <div style={{ fontWeight:600, fontSize:13, color:'#F0EDE8' }}>New to QuickServe?</div>
                <div style={{ fontSize:12, color:'#64748B', marginTop:2 }}>
                  {selfSignupEnabled ? 'Set up your stall in minutes — no credit card required' : 'Joining is by invitation from your mall — get in touch to get started'}
                </div>
              </div>
              {selfSignupEnabled ? (
                <a href="/vendor/subscribe"
                  style={{ padding:'9px 20px', borderRadius:8, background:'#F59E0B', color:'#000', fontWeight:700, fontSize:13, textDecoration:'none', whiteSpace:'nowrap' }}>
                  🚀 Start free →
                </a>
              ) : contactEmail ? (
                <a href={`mailto:${contactEmail}`}
                  style={{ padding:'9px 20px', borderRadius:8, background:'#F59E0B', color:'#000', fontWeight:700, fontSize:13, textDecoration:'none', whiteSpace:'nowrap' }}>
                  ✉️ Get in touch
                </a>
              ) : null}
            </div>
            <div style={{ marginTop:14, textAlign:'center', fontSize:12 }}>
              <a href="/staff" style={{ color:'#64748B' }}>Cashier or manager? Staff login →</a>
            </div>
          </>
        )}

        {/* Login form */}
        {selectedRole && selectedConfig && (
          <div style={{
            background: selectedConfig.bg,
            border: `1px solid ${selectedConfig.color}`,
            borderRadius:16, padding:32,
          }}>
            {/* Back button */}
            <button onClick={() => { setSelectedRole(null); setEmail(''); setPassword(''); setError(''); }}
              style={{ background:'none', border:'none', color:'#64748B', cursor:'pointer', fontSize:13, marginBottom:20, padding:0, display:'flex', alignItems:'center', gap:6 }}>
              ← Back
            </button>

            {/* Role header */}
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:28 }}>
              <span style={{ fontSize:36 }}>{selectedConfig.icon}</span>
              <div>
                <div style={{ fontWeight:700, fontSize:18, color:'#F0EDE8' }}>{selectedConfig.title}</div>
                <div style={{ fontSize:12, color:'#94A3B8', marginTop:2 }}>{selectedConfig.sub}</div>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={{ padding:'10px 14px', borderRadius:8, marginBottom:16,
                background:'#3d1a1a', border:'1px solid #A32D2D',
                color:'#F87171', fontSize:13 }}>
                ❌ {error}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:12, color:'#94A3B8', marginBottom:6, fontWeight:500 }}>Email</div>
                <input
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(''); }}
                  placeholder="your@email.com"
                  autoFocus
                  style={{ width:'100%', padding:'11px 14px', borderRadius:8, fontSize:14,
                    background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)',
                    color:'#F0EDE8', outline:'none' }}
                />
              </div>
              <div style={{ marginBottom:24 }}>
                <div style={{ fontSize:12, color:'#94A3B8', marginBottom:6, fontWeight:500 }}>Password</div>
                <input
                  type="password"
                  value={password}
                  onChange={e => { setPassword(e.target.value); setError(''); }}
                  placeholder="••••••••"
                  style={{ width:'100%', padding:'11px 14px', borderRadius:8, fontSize:14,
                    background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)',
                    color:'#F0EDE8', outline:'none' }}
                />
              </div>
              <button type="submit" disabled={loading}
                style={{ width:'100%', padding:13, borderRadius:10, border:'none',
                  background: selectedConfig.color, color:'#fff',
                  fontSize:15, fontWeight:700, cursor:loading?'not-allowed':'pointer',
                  opacity: loading ? .7 : 1 }}>
                {loading ? '⏳ Signing in...' : `Sign in as ${selectedConfig.title.replace(' Login','')}`}
              </button>
            </form>

            {/* Vendor sign-up shortcut */}
            {selectedRole === 'VENDOR' && (
              <div style={{ textAlign:'center', marginTop:18, fontSize:13, color:'#64748B' }}>
                Don't have an account?{' '}
                <a href="/vendor/subscribe" style={{ color:'#F59E0B', textDecoration:'none', fontWeight:600 }}>
                  Create one →
                </a>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
